import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useState } from "react";
import { HologramDialog } from "@/components/prints/hologram/HologramDialog";
import { Database } from "@/integrations/supabase/types";
import { Input } from "@/components/ui/input";
import { SmartPagination } from "@/components/ui/smart-pagination";

type CertificateWithPrints = Database['public']['Tables']['certificates']['Row'] & {
  holograms: Pick<Database['public']['Tables']['holograms']['Row'], 'hologram_code'>;
  prints: (Pick<Database['public']['Tables']['prints']['Row'], 
    'print_id' | 'edition_id' | 'edition_number' | 'media_id'> & {
    media: Pick<Database['public']['Tables']['media']['Row'], 'media_name'>;
    print_service_providers: Pick<Database['public']['Tables']['print_service_providers']['Row'], 'name'>;
    editions: Pick<Database['public']['Tables']['editions']['Row'], 
      'edition_id' | 'edition_name' | 'edition_limit' | 'total_count' | 'width' | 'height'> & {
      artworks: Pick<Database['public']['Tables']['artworks']['Row'], 
        'title' | 'preview' | 'creation_date' | 'art_technique' | 'creator_id'> & {
        creators: Pick<Database['public']['Tables']['creators']['Row'], 
          'name' | 'surname' | 'nick_name'>;
      };
    };
  })[];
}

const ITEMS_PER_PAGE = 10;

export default function Certificates() {
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateWithPrints | null>(null);
  const [hologramFilter, setHologramFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data: certificatesList, isLoading } = useQuery({
    queryKey: ["certificates-list", hologramFilter, currentPage],
    queryFn: async () => {
      console.log("Fetching certificates with filter:", hologramFilter, "page:", currentPage);
      
      let query = supabase
        .from("certificates")
        .select(`
          *,
          holograms (
            hologram_code
          ),
          prints!inner (
            print_id,
            edition_id,
            edition_number,
            media_id,
            media (
              media_name
            ),
            print_service_providers (
              name
            ),
            editions!inner (
              edition_id,
              edition_name,
              edition_limit,
              total_count,
              width,
              height,
              artwork_id,
              artworks!inner (
                title,
                preview,
                creation_date,
                art_technique,
                creator_id,
                creators (
                  name,
                  surname,
                  nick_name
                )
              )
            )
          )
        `, { count: 'exact' });

      if (hologramFilter) {
        query = query.ilike('hologram_number', `%${hologramFilter}%`);
      }

      // Get total count first
      const { count } = await query;
      
      // Add pagination
      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      
      const { data, error } = await query
        .order('issue_date', { ascending: false })
        .range(from, to);

      if (error) {
        console.error("Error fetching certificates:", error);
        throw error;
      }

      console.log("Fetched certificates:", data);
      return {
        certificates: data as CertificateWithPrints[],
        totalCount: count || 0
      };
    },
  });

  const totalPages = Math.ceil((certificatesList?.totalCount || 0) / ITEMS_PER_PAGE);

  const handleCertificateClick = (certificate: CertificateWithPrints) => {
    console.log("Opening certificate details:", certificate);
    setSelectedCertificate(certificate);
  };

  const getRowClassName = (certificate: CertificateWithPrints) => {
    const hasDiscrepancy = certificate.holograms?.hologram_code !== certificate.hologram_number;
    return `cursor-pointer hover:bg-muted/50 ${hasDiscrepancy ? 'bg-red-50' : ''}`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Certificates</h1>
          <div className="w-72">
            <Input
              placeholder="Filter by hologram number..."
              value={hologramFilter}
              onChange={(e) => {
                setHologramFilter(e.target.value);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
            />
          </div>
        </div>

        {isLoading ? (
          <div>Loading certificates...</div>
        ) : (
          <div className="space-y-4">
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hologram Number</TableHead>
                    <TableHead>Artwork / Edition</TableHead>
                    <TableHead>Creator</TableHead>
                    <TableHead>Edition Count</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Issued By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {certificatesList?.certificates.map((certificate) => {
                    const print = certificate.prints[0];
                    const edition = print.editions;
                    const artwork = edition.artworks;
                    const creator = artwork.creators;

                    console.log("Processing certificate:", {
                      certificate_id: certificate.certificate_id,
                      print_data: {
                        print_id: print.print_id,
                        edition_number: print.edition_number
                      },
                      edition_data: {
                        edition_id: edition.edition_id,
                        edition_limit: edition.edition_limit
                      }
                    });

                    const editionCount = `${print.edition_number}${
                      edition.edition_limit ? ` / ${edition.edition_limit}` : ""
                    }`;

                    return (
                      <TableRow 
                        key={certificate.certificate_id}
                        className={getRowClassName(certificate)}
                        onClick={() => handleCertificateClick(certificate)}
                      >
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{certificate.holograms?.hologram_code}</span>
                            <span className="text-sm text-gray-500">{certificate.hologram_number}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{artwork.title}</span>
                            <span className="text-sm text-gray-500">
                              {edition.edition_name}
                              {edition.width && edition.height && (
                                <span className="ml-1">
                                  ({edition.width} x {edition.height} cm)
                                </span>
                              )}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {creator && (
                            <>
                              {creator.nick_name ? (
                                <div>
                                  <span className="font-medium">{creator.nick_name}</span>
                                  <span className="text-sm text-gray-500 block">
                                    {creator.name} {creator.surname}
                                  </span>
                                </div>
                              ) : (
                                <span>{creator.name} {creator.surname}</span>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell>{editionCount}</TableCell>
                        <TableCell>
                          {new Date(certificate.issue_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{certificate.issued_by}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <SmartPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                className="justify-center"
              />
            )}
          </div>
        )}

        <HologramDialog
          open={!!selectedCertificate}
          onOpenChange={(open) => !open && setSelectedCertificate(null)}
          certificate={selectedCertificate}
        />
      </div>
    </AdminLayout>
  );
}
